import {
  bubblePendingApprovalsQueryKey,
  eventPendingTimesQueryKey,
  useEventGetPendingEventTimesTyped,
} from "@crowdcoursing/api/bubble-permissions";
import {
  useEventApproveEventTime,
  useEventBulkApproveEventTimes,
  useEventRejectEventTime,
} from "@crowdcoursing/api/openapi";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

import { useToast } from "~hooks/useToast";

const tApproveError = "Failed to approve event time";
const tApproveSuccess = "Event time approved";
const tBulkApproveError = "Failed to approve event times";
const tBulkApproveSuccess = "Event times approved";
const tRejectError = "Failed to reject event time";
const tRejectSuccess = "Event time rejected";

export function useEventTimeApprovals(
  eventId: string,
  options?: { bubbleId?: string | null; enabled?: boolean; onChanged?: () => void },
) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const enabled = (options?.enabled ?? true) && !!eventId;

  const { data: pendingTimes = [], status, refetch } = useEventGetPendingEventTimesTyped(eventId, { enabled });

  const invalidate = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: [...eventPendingTimesQueryKey, eventId] });
    if (options?.bubbleId) {
      await queryClient.invalidateQueries({ queryKey: [...bubblePendingApprovalsQueryKey, options.bubbleId] });
    }
    options?.onChanged?.();
  }, [eventId, options, queryClient]);

  const { mutateAsync: approveMutation, isPending: isApproving } = useEventApproveEventTime({
    onError: () => toast.error(tApproveError),
    onSuccess: async () => {
      toast.success(tApproveSuccess);
      await invalidate();
    },
  });

  const { mutateAsync: rejectMutation, isPending: isRejecting } = useEventRejectEventTime({
    onError: () => toast.error(tRejectError),
    onSuccess: async () => {
      toast.success(tRejectSuccess);
      await invalidate();
    },
  });

  const { mutateAsync: bulkApproveMutation, isPending: isBulkApproving } = useEventBulkApproveEventTimes({
    onError: () => toast.error(tBulkApproveError),
    onSuccess: async () => {
      toast.success(tBulkApproveSuccess);
      await invalidate();
    },
  });

  const approveTime = useCallback(
    async (meetingTimeId: string) => {
      await approveMutation({
        body: {},
        pathParams: { eventId, meetingTimeId },
      });
    },
    [approveMutation, eventId],
  );

  const rejectTime = useCallback(
    async (meetingTimeId: string) => {
      await rejectMutation({
        pathParams: { eventId, meetingTimeId },
      });
    },
    [eventId, rejectMutation],
  );

  const approveAll = useCallback(async () => {
    const meetingTimeIds = pendingTimes.map((time) => time.id).filter((id): id is string => Boolean(id));
    if (meetingTimeIds.length === 0) {
      return;
    }
    await bulkApproveMutation({
      body: { meetingTimeIds },
      pathParams: { eventId },
    });
  }, [bulkApproveMutation, eventId, pendingTimes]);

  return {
    approveAll,
    approveTime,
    isApproving,
    isBulkApproving,
    isRejecting,
    pendingCount: pendingTimes.length,
    pendingTimes,
    refetch,
    rejectTime,
    status,
  };
}
